import type { ViewRData } from "../../types/unity";
import { SAVE_PERSIST_FAILED_EVENT } from "./saveEvents";
import { buildUnitySaveAttemptId } from "../../utils/unitySaveAttempt";
import { rememberViewrVdbGcsPath } from "./viewrVdbGcsPathSession";

export const EDITOR_IMPORT_PRECONVERSION_SAVE_REASON =
  "editor_import_preconversion";
export const UNITY_SAVE_JSON_READY_EVENT = "viewr:unitySaveJsonReady";

export type EditorImportHybridConversionFields = {
  viewr_gcs_path: string;
  vdb_gcs_path: string;
  space_index: number;
  vse_index: number;
};

export type RequestEditorImportSaveSnapshot = (payload: {
  reason: string;
  saveAttemptId: string;
  conversionJobId: string;
}) => Promise<void>;

type UnitySaveJsonReadyDetail = {
  saveAttemptId?: string;
  vdb_gcs_path?: string;
  vdbGcsPath?: string;
};

// Unity VDB pack + signed URL + GCS PUT 상한(120s)과 맞춘다.
const DEFAULT_EDITOR_IMPORT_SAVE_TIMEOUT_MS = 120000;

const normalizeStoragePath = (value: string | null | undefined, field: string): string => {
  const normalized = (value ?? "").trim();
  if (!/^(gs|s3):\/\/[^/]+\/.+/.test(normalized)) {
    throw new Error(`${field} must be a complete gs:// or s3:// storage URI.`);
  }
  return normalized;
};

export function resolveEditorImportHybridConversionFields({
  viewRData,
  viewrGCSPath,
  vdbGcsPath,
  targetSpaceIndex,
}: {
  viewRData: ViewRData | null | undefined;
  viewrGCSPath: string | null | undefined;
  vdbGcsPath: string | null | undefined;
  targetSpaceIndex: number | null | undefined;
}): EditorImportHybridConversionFields {
  const viewr_gcs_path = normalizeStoragePath(viewrGCSPath, "viewr_gcs_path");
  const vdb_gcs_path = normalizeStoragePath(vdbGcsPath, "vdb_gcs_path");

  if (
    typeof targetSpaceIndex !== "number" ||
    !Number.isFinite(targetSpaceIndex) ||
    targetSpaceIndex < 0
  ) {
    throw new Error("space_index must be a non-negative number.");
  }

  const space_index = Math.trunc(targetSpaceIndex);
  const space = viewRData?.spaces?.[space_index];
  if (!space) {
    throw new Error("Target Space is not available for object import.");
  }

  rememberViewrVdbGcsPath(viewr_gcs_path, vdb_gcs_path);

  return {
    viewr_gcs_path,
    vdb_gcs_path,
    space_index,
    vse_index: Array.isArray(space.vseList) ? space.vseList.length : 0,
  };
}

export function waitForUnitySaveJsonReady({
  saveAttemptId,
  timeoutMs = DEFAULT_EDITOR_IMPORT_SAVE_TIMEOUT_MS,
  signal,
  assertCurrent,
}: {
  saveAttemptId: string;
  timeoutMs?: number;
  signal?: AbortSignal;
  assertCurrent?: () => void;
}): Promise<{ vdbGcsPath: string }> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Save wait aborted", "AbortError"));
      return;
    }

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      if (contextIntervalId != null) window.clearInterval(contextIntervalId);
      window.removeEventListener(UNITY_SAVE_JSON_READY_EVENT, onReady);
      window.removeEventListener(SAVE_PERSIST_FAILED_EVENT, onPersistFailed);
      signal?.removeEventListener("abort", onAbort);
    };

    const settleReject = (error: Error | DOMException) => {
      cleanup();
      reject(error);
    };

    const timeoutId = window.setTimeout(() => {
      settleReject(new Error("Unity save snapshot wait timed out."));
    }, timeoutMs);
    const contextIntervalId = assertCurrent == null ? null : window.setInterval(() => {
      try {
        assertCurrent();
      } catch (error) {
        settleReject(error instanceof Error ? error : new Error("Save wait context ended."));
      }
    }, 100);

    const onReady = (event: Event) => {
      const detail = (event as CustomEvent<UnitySaveJsonReadyDetail>).detail ?? {};
      if (detail.saveAttemptId !== saveAttemptId) return;

      const vdbGcsPath = (detail.vdb_gcs_path || detail.vdbGcsPath || "").trim();
      if (!/^(gs|s3):\/\/[^/]+\/.+/.test(vdbGcsPath)) {
        settleReject(new Error("Unity save snapshot did not include vdb_gcs_path."));
        return;
      }

      cleanup();
      resolve({ vdbGcsPath });
    };

    const onPersistFailed = (event: Event) => {
      const detail = (event as CustomEvent<{ saveAttemptId?: string; error?: string }>)
        .detail;
      if (detail?.saveAttemptId !== saveAttemptId) return;
      settleReject(
        new Error(
          detail?.error?.trim() ||
            "Unity save snapshot failed before VDB upload completed.",
        ),
      );
    };

    const onAbort = () => {
      settleReject(new DOMException("Save wait aborted", "AbortError"));
    };

    window.addEventListener(UNITY_SAVE_JSON_READY_EVENT, onReady);
    window.addEventListener(SAVE_PERSIST_FAILED_EVENT, onPersistFailed);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export async function prepareEditorImportHybridConversionFields({
  viewRData,
  viewrGCSPath,
  targetSpaceIndex,
  conversionJobId,
  requestSaveSnapshot,
  timeoutMs = DEFAULT_EDITOR_IMPORT_SAVE_TIMEOUT_MS,
  assertCurrent,
}: {
  viewRData: ViewRData | null | undefined;
  viewrGCSPath: string | null | undefined;
  targetSpaceIndex: number | null | undefined;
  conversionJobId: string;
  requestSaveSnapshot: RequestEditorImportSaveSnapshot;
  timeoutMs?: number;
  assertCurrent?: () => void;
}): Promise<EditorImportHybridConversionFields> {
  const normalizedConversionJobId = conversionJobId.trim();
  if (!normalizedConversionJobId) {
    throw new Error("conversionJobId is required for editor import VDB snapshot.");
  }

  const saveAttemptId = buildUnitySaveAttemptId(
    EDITOR_IMPORT_PRECONVERSION_SAVE_REASON,
  );
  const abortController = new AbortController();
  const readyPromise = waitForUnitySaveJsonReady({
    saveAttemptId,
    timeoutMs,
    signal: abortController.signal,
    assertCurrent,
  });
  readyPromise.catch(() => undefined);

  try {
    await requestSaveSnapshot({
      reason: EDITOR_IMPORT_PRECONVERSION_SAVE_REASON,
      saveAttemptId,
      conversionJobId: normalizedConversionJobId,
    });

    const ready = await readyPromise;

    return resolveEditorImportHybridConversionFields({
      viewRData,
      viewrGCSPath,
      vdbGcsPath: ready.vdbGcsPath,
      targetSpaceIndex,
    });
  } catch (error) {
    abortController.abort();
    await Promise.allSettled([readyPromise]);
    throw error;
  }
}
